import { auth, db, collection, doc, setDoc, getDoc, updateDoc, deleteDoc, query, getDocs, serverTimestamp, limit } from '../firebase/config';
import { toast } from 'react-hot-toast';

/* -------------------- FIREBASE PRODUCT SERVICE -------------------- */
class FirebaseProductService {
  // Get current user ID
  static getCurrentUserId() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated. Please login again.');
    }
    return user.uid;
  }

  // Get products collection reference
  static getProductsCollection() {
    return collection(db, 'products');
  }

  // Get specific product document reference
  static getProductDoc(productId) {
    return doc(db, 'products', productId);
  }

  // Convert image to base64 with heavy compression for Firestore
  static async compressImageForFirestore(file) {
    return new Promise((resolve, reject) => {
      try {
        console.log('Starting image compression...', file.name, file.size);
        
        // Skip compression for very small files
        if (file.size < 50000) { // Less than 50KB
          const reader = new FileReader();
          reader.onload = (e) => {
            console.log('File is small, using original:', e.target.result.length);
            resolve(e.target.result);
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            
            // Calculate new dimensions (max 400px on longest side)
            const MAX_SIZE = 400;
            let width = img.width;
            let height = img.height;
            
            if (width > height) {
              if (width > MAX_SIZE) {
                height *= MAX_SIZE / width;
                width = MAX_SIZE;
              }
            } else {
              if (height > MAX_SIZE) {
                width *= MAX_SIZE / height;
                height = MAX_SIZE;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Try different quality levels to keep under 100KB
            let quality = 0.6;
            let base64String;
            let attempts = 0;
            
            do {
              base64String = canvas.toDataURL('image/jpeg', quality);
              console.log(`Compression attempt ${attempts + 1}: Quality=${quality}, Size=${base64String.length}`);
              quality -= 0.1;
              attempts++;
            } while (base64String.length > 100000 && quality > 0.2 && attempts < 5);
            
            if (base64String.length > 100000) {
              // Last resort: use placeholder
              console.log('Image still too large, using placeholder');
              resolve(`https://via.placeholder.com/300x200?text=${encodeURIComponent(file.name.split('.')[0])}`);
            } else {
              console.log('Compression successful:', base64String.length);
              resolve(base64String);
            }
          };
          img.onerror = () => {
            console.log('Image load failed, using placeholder');
            resolve(`https://via.placeholder.com/300x200?text=${encodeURIComponent(file.name.split('.')[0])}`);
          };
          img.src = e.target.result;
        };
        reader.onerror = () => {
          console.log('File read failed, using placeholder');
          resolve(`https://via.placeholder.com/300x200?text=${encodeURIComponent(file.name.split('.')[0])}`);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Compression error:', error);
        resolve(`https://via.placeholder.com/300x200?text=${encodeURIComponent(file.name.split('.')[0])}`);
      }
    });
  }

  // Get all products
  static async getAllProducts(options = {}) {
    try {
      const { 
        limitCount = 100,
        userId = null
      } = options;
      
      const productsRef = this.getProductsCollection();
      let q = query(productsRef, limit(limitCount));
      
      const querySnapshot = await getDocs(q);
      
      const products = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        products.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
        });
      });
      
      // Manual filtering in JavaScript
      let filteredProducts = [...products];
      
      if (userId) {
        filteredProducts = filteredProducts.filter(p => p.sellerId === userId);
      }
      
      // Manual sorting by createdAt (newest first)
      filteredProducts.sort((a, b) => {
        const dateA = a.createdAtTimestamp || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const dateB = b.createdAtTimestamp || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return dateB - dateA;
      });
      
      return {
        products: filteredProducts,
        lastDoc: null,
        hasMore: false
      };
      
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error(`Failed to load products: ${error.message}`);
    }
  }

  // Get products for current user
  static async getUserProducts() {
    try {
      const userId = this.getCurrentUserId();
      const result = await this.getAllProducts({ userId });
      return result;
    } catch (error) {
      console.error('Error fetching user products:', error);
      throw new Error(`Failed to load your products: ${error.message}`);
    }
  }

  // Get product by ID
  static async getProductById(productId) {
    try {
      const productDoc = this.getProductDoc(productId);
      const docSnap = await getDoc(productDoc);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw new Error(`Failed to load product: ${error.message}`);
    }
  }

  // Add new product - Store compressed images in Firestore
  static async addProduct(productData) {
    try {
      console.log('=== STARTING addProduct ===');
      console.log('Product data received:', productData);
      
      const userId = this.getCurrentUserId();
      const userEmail = auth.currentUser?.email || '';
      const userName = auth.currentUser?.displayName || userEmail.split('@')[0] || 'Unknown Seller';
      
      console.log('User info:', { userId, userEmail, userName });
      
      // Generate product ID
      const productId = `product_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log('Generated product ID:', productId);
      
      const productRef = this.getProductDoc(productId);
      
      // Process images - compress and store in Firestore
      let storedImages = [];
      
      if (productData.images && productData.images.length > 0) {
        console.log(`Processing ${productData.images.length} images...`);
        
        for (let i = 0; i < productData.images.length; i++) {
          const imageFile = productData.images[i];
          if (imageFile instanceof File) {
            try {
              console.log(`Processing image ${i + 1}:`, imageFile.name, imageFile.size, 'bytes');
              
              // Compress image for Firestore
              const compressedImageData = await this.compressImageForFirestore(imageFile);
              
              storedImages.push({
                id: i + 1,
                name: imageFile.name,
                type: imageFile.type,
                size: imageFile.size,
                isMain: i === 0,
                uploadedAt: new Date().toISOString(),
                data: compressedImageData,
                hasImageData: true
              });
              
              console.log(`✅ Image ${i + 1} processed:`, typeof compressedImageData === 'string' ? compressedImageData.substring(0, 50) + '...' : compressedImageData);
            } catch (error) {
              console.error(`❌ Failed to process image ${i + 1}:`, error);
              // Store placeholder
              storedImages.push({
                id: i + 1,
                name: imageFile.name,
                type: imageFile.type,
                size: imageFile.size,
                isMain: i === 0,
                uploadedAt: new Date().toISOString(),
                data: `https://via.placeholder.com/300x200?text=${encodeURIComponent(imageFile.name.split('.')[0])}`,
                hasImageData: false
              });
            }
          }
        }
      } else {
        console.log('No images to process');
      }
      
      // Prepare specifications as array
      const specificationsArray = Array.isArray(productData.specifications) 
        ? productData.specifications 
        : (productData.specifications || '').split('\n')
            .map(spec => spec.trim())
            .filter(spec => spec.length > 0);
      
      // Parse numeric values
      const quantity = parseFloat(productData.quantity) || 0;
      const price = parseFloat(productData.price) || 0;
      const moq = parseFloat(productData.moq) || 1; // NEW: Parse MOQ with default 1
      
      // Create product data for Firestore - Status is always PENDING
      const productToSave = {
        name: productData.name || '',
        category: productData.category || '',
        description: productData.description || '',
        specifications: specificationsArray,
        quantity: quantity,
        unit: productData.unit || 'PCS',
        price: price,
        currency: productData.currency || 'INR',
        gst: productData.gst || '',
        moq: moq, // NEW: Add MOQ field
        warranty: productData.warranty || 'No Warranty', // NEW: Add warranty field with default
        status: 'PENDING',
        colors: productData.colors || '',
        images: storedImages,
        sellerId: userId,
        sellerName: productData.sellerName || userName,
        sellerEmail: userEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        views: 0,
        favorites: 0,
        // Add timestamp fields that can be sorted
        createdAtTimestamp: Date.now(),
        updatedAtTimestamp: Date.now(),
        // Legacy fields
        hasImages: storedImages.length > 0,
        imagesCount: storedImages.length,
        // Admin approval fields
        approvedBy: null,
        approvedAt: null,
        rejectedBy: null,
        rejectedAt: null,
        rejectionReason: null
      };
      
      console.log('Product data to save to Firestore:', {
        ...productToSave,
        images: `${storedImages.length} images`,
        status: 'PENDING',
        colors: productToSave.colors,
        gst: productToSave.gst,
        moq: productToSave.moq, // Log MOQ
        warranty: productToSave.warranty // Log warranty
      });
      
      // Save product data to Firestore
      await setDoc(productRef, productToSave);
      
      console.log('✅ Product saved successfully to Firestore with PENDING status, GST:', productData.gst, 'MOQ:', moq, 'Warranty:', productData.warranty);
      
      // Create response object
      const savedProduct = {
        id: productId,
        ...productToSave,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      toast.success(`Product "${productData.name}" submitted for admin approval!`);
      console.log('=== addProduct COMPLETED SUCCESSFULLY ===');
      return savedProduct;
      
    } catch (error) {
      console.error('❌ Error adding product:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      if (error.message.includes('Payload size exceeds')) {
        toast.error('Product data too large. Try with smaller images or fewer images.');
      } else {
        toast.error(`Failed to add product: ${error.message}`);
      }
      throw error;
    }
  }

  // Update product - Only allow updating PENDING products
  static async updateProduct(productId, updatedData) {
    try {
      const productRef = this.getProductDoc(productId);
      const currentProduct = await this.getProductById(productId);
      
      if (!currentProduct) {
        throw new Error('Product not found');
      }
      
      // Check if current user is the seller
      const userId = this.getCurrentUserId();
      if (currentProduct.sellerId !== userId) {
        throw new Error('You are not authorized to update this product');
      }
      
      // Check if product is PENDING (only PENDING products can be updated)
      if (currentProduct.status !== 'PENDING') {
        throw new Error('Only PENDING products can be updated. This product has already been reviewed.');
      }
      
      // Prepare specifications
      const specificationsArray = Array.isArray(updatedData.specifications) 
        ? updatedData.specifications 
        : typeof updatedData.specifications === 'string'
          ? updatedData.specifications.split('\n').filter(s => s.trim())
          : currentProduct.specifications;
      
      // Parse numeric values
      const quantity = parseFloat(updatedData.quantity) || currentProduct.quantity || 0;
      const price = parseFloat(updatedData.price) || currentProduct.price || 0;
      const moq = parseFloat(updatedData.moq) || currentProduct.moq || 1; // NEW: Parse MOQ
      
      // Create update data - Status remains PENDING after update
      const updateData = {
        name: updatedData.name || currentProduct.name,
        category: updatedData.category || currentProduct.category,
        description: updatedData.description || currentProduct.description,
        specifications: specificationsArray,
        quantity: quantity,
        unit: updatedData.unit || currentProduct.unit || 'PCS',
        price: price,
        currency: updatedData.currency || currentProduct.currency || 'INR',
        gst: updatedData.gst || currentProduct.gst || '',
        moq: moq, // NEW: Update MOQ
        warranty: updatedData.warranty || currentProduct.warranty || 'No Warranty', // NEW: Update warranty
        status: 'PENDING',
        colors: updatedData.colors || currentProduct.colors || '',
        updatedAt: serverTimestamp(),
        updatedAtTimestamp: Date.now()
      };
      
      // Update in Firestore
      await updateDoc(productRef, updateData);
      
      const updatedProduct = {
        id: productId,
        ...currentProduct,
        ...updateData,
        updatedAt: new Date()
      };
      
      toast.success('Product updated successfully! Status reset to PENDING for admin review.');
      return updatedProduct;
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product. Please try again.');
      throw new Error(`Failed to update product: ${error.message}`);
    }
  }

  // Delete product - Only allow deleting PENDING products
  static async deleteProduct(productId) {
    try {
      const productRef = this.getProductDoc(productId);
      const product = await this.getProductById(productId);
      
      if (!product) {
        throw new Error('Product not found');
      }
      
      // Check if current user is the seller
      const userId = this.getCurrentUserId();
      if (product.sellerId !== userId) {
        throw new Error('You are not authorized to delete this product');
      }
      
      // Check if product is PENDING (only PENDING products can be deleted)
      if (product.status !== 'PENDING') {
        throw new Error('Only PENDING products can be deleted. This product has already been reviewed.');
      }
      
      // Delete product document
      await deleteDoc(productRef);
      
      toast.success('Product deleted successfully!');
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product. Please try again.');
      throw new Error(`Failed to delete product: ${error.message}`);
    }
  }
}

export default FirebaseProductService;